import React, { useEffect, useState, useCallback } from "react";
import { Drawer, Typography, Button, IconButton, TextField } from "@mhome/ui";
import CircularProgress from "@mui/material/CircularProgress";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useRecoilValue } from "recoil";
import {
  safeAreaInsetsState,
  wsReadyState,
  scopesState,
} from "../global-state";
import { useWebSocket } from "../ws/websocket";
import {
  updateWidgetData,
  updateWidgetSetting,
  getDefaultWidgets,
} from "../service/widget-service";
import { useIsDarkMode } from "../lib/utils";

const SpaceDrawer = ({
  open,
  onClose,
  currentSpace,
  onSpaceSwitch,
  spaceSwitching,
}) => {
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);
  const { sendAndWaitWs } = useWebSocket();
  const [homeModes, setHomeModes] = useState([]);
  const [editingWidget, setEditingWidget] = useState(null);
  const [newOptionText, setNewOptionText] = useState("");
  const scopes = useRecoilValue(scopesState);
  const wsReady = useRecoilValue(wsReadyState);
  const actualMode = useIsDarkMode();
  const isDark = actualMode === "dark";

  const fetchHomeModes = useCallback(async () => {
    if (!currentSpace?.id || !wsReady) {
      return;
    }

    try {
      const response = await getDefaultWidgets(sendAndWaitWs);
      if (response.error) {
        console.error("Error fetching home modes:", response.error);
        setHomeModes([]);
        return;
      }
      const widgets = response?.widgets || [];

      // Sort widgets: homeMode first, then extendedHomeMode
      const sortedWidgets = widgets.sort((a, b) => {
        if (a.tag === "homeMode") return -1;
        if (b.tag === "homeMode") return 1;
        if (a.tag === "aMode") return -1;
        if (b.tag === "extendedHomeMode") return 1;
        return 0;
      });

      setHomeModes(sortedWidgets);
    } catch (error) {
      console.error("Error fetching home modes:", error);
      setHomeModes([]);
    }
  }, [wsReady, currentSpace?.id]);

  // Fetch home modes data when drawer opens
  useEffect(() => {
    if (open && wsReady) {
      fetchHomeModes();
    }
  }, [open, wsReady, fetchHomeModes]);

  // Handle option selection for widgets
  const handleOptionSelect = async (widget, selectedOption) => {
    if (selectedOption === widget.value) {
      return; // Don't update if it's the same value
    }

    // Call the API to update the widget
    const widgetUpdateData = {
      ...widget,
      value: selectedOption,
    };

    console.log(
      `Updating ${widget.tag} to ${selectedOption}:`,
      widgetUpdateData
    );

    try {
      const response = await updateWidgetData(sendAndWaitWs, [
        widgetUpdateData,
      ]);
      if (response && !response.error) {
        await fetchHomeModes();
      } else {
        console.error("Failed to update widget data");
      }
    } catch (error) {
      console.error("Error updating widget:", error);
    }
  };

  // Handle toggling edit mode for a widget
  const handleToggleEdit = (widget) => {
    if (editingWidget === widget.id) {
      // Exit edit mode
      setEditingWidget(null);
      setNewOptionText("");
    } else {
      // Enter edit mode
      setEditingWidget(widget.id);
      setNewOptionText("");
    }
  };

  // Handle adding a new option
  const handleAddOption = async (widget) => {
    if (!newOptionText.trim()) return;

    const updatedOptions = [...(widget.options || []), newOptionText.trim()];
    const widgetUpdateData = {
      ...widget,
      options: updatedOptions,
    };

    console.log(
      `Adding option "${newOptionText}" to ${widget.tag}:`,
      widgetUpdateData
    );

    try {
      const response = await updateWidgetSetting(
        sendAndWaitWs,
        widgetUpdateData
      );
      if (response && !response.error) {
        setNewOptionText("");
        await fetchHomeModes();
      } else {
        console.error("Failed to add option");
      }
    } catch (error) {
      console.error("Error adding option:", error);
    }
  };

  // Handle deleting an option
  const handleDeleteOption = async (widget, optionToDelete) => {
    if (widget.options.length <= 1) {
      console.warn("Cannot delete the last option");
      return;
    }

    if (widget.value === optionToDelete) {
      console.warn("Cannot delete the currently selected option");
      return;
    }

    const updatedOptions = widget.options.filter(
      (option) => option !== optionToDelete
    );
    const widgetUpdateData = {
      ...widget,
      options: updatedOptions,
    };

    console.log(
      `Deleting option "${optionToDelete}" from ${widget.tag}:`,
      widgetUpdateData
    );

    try {
      const response = await updateWidgetSetting(
        sendAndWaitWs,
        widgetUpdateData
      );
      if (response && !response.error) {
        await fetchHomeModes();
      } else {
        console.error("Failed to delete option");
      }
    } catch (error) {
      console.error("Error deleting option:", error);
    }
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      transitionDuration={300}
      style={{
        zIndex: "calc(var(--z-index-drawer) + 1)",
      }}
      PaperProps={{
        style: {
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          backgroundColor: isDark
            ? "rgba(0, 0, 0, 0.4)"
            : "rgba(255, 255, 255, 0.7)",
          backdropFilter: "blur(10px)",
          maxHeight: "80vh",
          paddingBottom: `${safeAreaInsets.bottom}px`,
        },
      }}
    >
      <div
        style={{
          width: "100%",
          padding: "16px",
          paddingTop: "24px",
          paddingBottom: "32px",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Space
          </Typography>
        </div>

        {/* Space buttons */}
        {spaceSwitching ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              paddingTop: "32px",
              paddingBottom: "32px",
            }}
          >
            <CircularProgress size={32} />
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: "8px",
              paddingTop: 0,
              paddingBottom: "8px",
            }}
          >
            {scopes.map((space) => {
              const isCurrentSpace =
                currentSpace && space.id === currentSpace.id;

              return (
                <Button
                  key={space.id}
                  onClick={() => onSpaceSwitch(space)}
                  disabled={isCurrentSpace}
                  variant={isCurrentSpace ? "contained" : "outlined"}
                  style={{
                    minWidth: "120px",
                    flexShrink: 0,
                    justifyContent: "center",
                    textAlign: "center",
                    paddingTop: "12px",
                    paddingBottom: "12px",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    textTransform: "none",
                    fontSize: "0.875rem",
                    fontWeight: isCurrentSpace ? 600 : 400,
                    backgroundColor: isCurrentSpace
                      ? "hsl(var(--primary))"
                      : "transparent",
                    borderColor: isCurrentSpace
                      ? "hsl(var(--primary))"
                      : "hsl(var(--border))",
                    color: isCurrentSpace
                      ? "hsl(var(--primary-foreground))"
                      : "hsl(var(--foreground))",
                  }}
                  onMouseEnter={(e) => {
                    if (!isCurrentSpace) {
                      e.currentTarget.style.backgroundColor =
                        "hsl(var(--accent))";
                      e.currentTarget.style.borderColor = "hsl(var(--primary))";
                    } else {
                      e.currentTarget.style.backgroundColor =
                        "hsl(var(--primary-foreground))";
                      e.currentTarget.style.borderColor =
                        "hsl(var(--primary-foreground))";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isCurrentSpace) {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.borderColor = "hsl(var(--border))";
                    } else {
                      e.currentTarget.style.backgroundColor =
                        "hsl(var(--primary))";
                      e.currentTarget.style.borderColor = "hsl(var(--primary))";
                    }
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="body2"
                      style={{
                        fontWeight: isCurrentSpace ? 600 : 400,
                        color: "inherit",
                      }}
                    >
                      {space.name}
                    </Typography>
                  </div>
                </Button>
              );
            })}
          </div>
        )}

        {/* Home modes section */}
        <div style={{ marginTop: "24px" }}>
          {homeModes.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "24px",
              }}
            >
              {homeModes.map((widget) => (
                <div
                  key={widget.id}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {/* Widget title with edit button */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      style={{
                        fontWeight: 500,
                        color: "hsl(var(--foreground))",
                        textTransform: "capitalize",
                      }}
                    >
                      {widget.tag === "homeMode"
                        ? "Home Mode"
                        : widget.tag === "activityMode"
                        ? "Activity Mode"
                        : widget.tag}
                    </Typography>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleToggleEdit(widget)}
                        style={{
                          color:
                            editingWidget === widget.id
                              ? "hsl(var(--primary))"
                              : "hsl(var(--muted-foreground))",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor =
                            "hsl(var(--accent))";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        {editingWidget === widget.id ? (
                          <EditOffIcon fontSize="small" />
                        ) : (
                          <EditIcon fontSize="small" />
                        )}
                      </IconButton>
                    </div>
                  </div>

                  {/* Widget options as buttons */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      {widget.options?.map((option) => {
                        const isSelected = option === widget.value;
                        const isEditing = editingWidget === widget.id;

                        return (
                          <div
                            key={option}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <Button
                              variant={isSelected ? "contained" : "outlined"}
                              size="small"
                              onClick={() =>
                                !isEditing && handleOptionSelect(widget, option)
                              }
                              disabled={isEditing}
                              style={{
                                minWidth: "auto",
                                paddingLeft: "16px",
                                paddingRight: "16px",
                                paddingTop: "6px",
                                paddingBottom: "6px",
                                boxShadow: "none",
                                textTransform: "none",
                                fontSize: "0.875rem",
                                fontWeight: isSelected ? 600 : 400,
                                backgroundColor: isSelected
                                  ? "hsl(var(--primary))"
                                  : "transparent",
                                borderColor: isSelected
                                  ? "hsl(var(--primary))"
                                  : "hsl(var(--border))",
                                color: isSelected
                                  ? "hsl(var(--primary-foreground))"
                                  : "hsl(var(--foreground))",
                                opacity: isEditing ? 0.7 : 1,
                              }}
                              onMouseEnter={(e) => {
                                if (!isEditing) {
                                  if (isSelected) {
                                    e.currentTarget.style.backgroundColor =
                                      "hsl(var(--primary-foreground))";
                                    e.currentTarget.style.borderColor =
                                      "hsl(var(--primary-foreground))";
                                  } else {
                                    e.currentTarget.style.backgroundColor =
                                      "hsl(var(--accent))";
                                    e.currentTarget.style.borderColor =
                                      "hsl(var(--primary))";
                                  }
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isEditing) {
                                  if (isSelected) {
                                    e.currentTarget.style.backgroundColor =
                                      "hsl(var(--primary))";
                                    e.currentTarget.style.borderColor =
                                      "hsl(var(--primary))";
                                  } else {
                                    e.currentTarget.style.backgroundColor =
                                      "transparent";
                                    e.currentTarget.style.borderColor =
                                      "hsl(var(--border))";
                                  }
                                }
                              }}
                            >
                              {option}
                            </Button>

                            {isEditing && (
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleDeleteOption(widget, option)
                                }
                                disabled={
                                  widget.options.length <= 1 ||
                                  option === widget.value
                                }
                                style={{
                                  color: "hsl(var(--destructive))",
                                }}
                                onMouseEnter={(e) => {
                                  if (
                                    !(
                                      widget.options.length <= 1 ||
                                      option === widget.value
                                    )
                                  ) {
                                    e.currentTarget.style.backgroundColor =
                                      "hsl(var(--destructive) / 0.12)";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "transparent";
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Add new option section - only show when editing */}
                    {editingWidget === widget.id && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginTop: "8px",
                        }}
                      >
                        <TextField
                          size="small"
                          placeholder="New option name"
                          value={newOptionText}
                          onChange={(e) => setNewOptionText(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === "Enter" && newOptionText.trim()) {
                              handleAddOption(widget);
                            }
                          }}
                          style={{
                            flexGrow: 1,
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleAddOption(widget)}
                          disabled={!newOptionText.trim()}
                          style={{
                            color: "hsl(var(--primary))",
                          }}
                          onMouseEnter={(e) => {
                            if (newOptionText.trim()) {
                              e.currentTarget.style.backgroundColor =
                                "hsl(var(--primary) / 0.12)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default SpaceDrawer;
